/**
 * Bring decks' card bodies into memory from THIS DEVICE's storage only — no
 * server request, no activity bump — and report the wait.
 *
 * For code that needs a deck's LOCAL rows resident before it can act on
 * them, but must not present that as the user opening the deck: the
 * study-group mirror merge (`use-seed-sg-mirror.ts`) overlays the member's
 * personal fields from the local rows onto the group's shared cards, and
 * under lazy card hydration those rows sit on disk until read. Bumping
 * activity here would keep every mirror of every visited group warm
 * server-side forever; asking the server for one that has no local rows would
 * fetch a set the merge is about to write anyway.
 *
 * `isLoading` is false without an effect tick when every deck is already
 * resident or already disk-loaded (`diskLoadedDeckIds` — memory ⊇ disk, so a
 * second read would find nothing new), true while any deck is being read.
 * Consumers put it in their effect deps so the guarded merge re-runs once the
 * rows are in.
 *
 * It is DERIVED from the store during render, like `useHydrateColdDecks`: a
 * flag an effect writes reads stale on the commit where the deck set changes,
 * and the merge that depends on it would run against rows that are not in yet.
 * A read that THROWS (storage unavailable) ends the wait rather than pinning
 * it — otherwise the guarded merge would never re-run at all.
 */

import { useEffect, useMemo, useState } from 'react';

import { loadDecksFromDisk } from './internal/deck-disk-load';
import { deckCardsResident } from './internal/deck-residency';
import { type FlashkaState, useFlashkaStore } from './internal/store/flashka-store';
import { reportSyncStorageError } from './internal/store/store-utils';

function pendingDiskLoads(state: FlashkaState, ids: readonly string[]): string[] {
  return ids.filter(
    (id) => !deckCardsResident(state, id) && !state.diskLoadedDeckIds.has(id),
  );
}

function hasPendingDiskLoad(state: FlashkaState, ids: readonly string[]): boolean {
  for (const id of ids) {
    if (!deckCardsResident(state, id) && !state.diskLoadedDeckIds.has(id)) return true;
  }
  return false;
}

export function useHydrateDeckCardsFromDisk(deckIds: readonly string[]): {
  isLoading: boolean;
} {
  const deckKey = useMemo(() => [...deckIds].sort().join(','), [deckIds]);
  // Read during render, so a changed deck set reports the wait on the very
  // commit it changes — not one effect tick later.
  const pending = useFlashkaStore((s) =>
    hasPendingDiskLoad(s, deckKey ? deckKey.split(',') : []),
  );
  // The set whose read has finished (or had nothing to read, or threw).
  const [settledKey, setSettledKey] = useState<string | null>(null);

  useEffect(() => {
    const ids = deckKey ? deckKey.split(',') : [];
    const settle = () => setSettledKey(deckKey);
    const pendingIds = pendingDiskLoads(useFlashkaStore.getState(), ids);
    if (pendingIds.length === 0) {
      settle();
      return;
    }
    let cancelled = false;
    void loadDecksFromDisk(pendingIds, () => !cancelled)
      .catch((err) => {
        // Storage itself failed. End the wait anyway: `isLoading` is an effect
        // dependency of the guarded mirror merge, so a stuck `true` is a merge
        // that never runs again.
        reportSyncStorageError(err, 'hydrate-deck-cards-from-disk');
      })
      .finally(() => {
        if (!cancelled) settle();
      });
    return () => {
      cancelled = true;
    };
  }, [deckKey]);

  const isLoading = pending && settledKey !== deckKey;
  return useMemo(() => ({ isLoading }), [isLoading]);
}
